import { useEffect, useRef, useCallback, useState } from 'react';
import { AxiosInstance } from 'axios';
import { useSocket } from 'socket.io-react-hook';
import { _getToken, positionOpenerAxiosApi } from 'src/Config/axiosApi';
import { resolveOpenerApi } from '../api/openerEndpoint';
import {
  OpenerGroupPodState,
  useOpenerGroupsPodState
} from '../api/useOpenerGroupPods';
import {
  GroupSelection,
  groupSelectionKey
} from '../components/entry/groupFilter';
import {
  EnrichedEntryStatus,
  GroupCostSummary,
  OpeningPageState,
  TopOfBook,
  VenuePositionsMeta,
  VenuePositionsMetaFrame
} from '../types/entry.types';
import {
  createLiveStore,
  LiveStore
} from '../live/createLiveStore';
import { createValueStore, ValueStore } from '../live/createValueStore';
import { useFlushOnVisible } from '../live/useFlushOnVisible';
import {
  LatencySample,
  recordReceived,
  useLatencyTracker
} from './useLatencyTracker';

export type { EnrichedEntryStatus } from '../types/entry.types';

export interface EntryExecutionEvent {
  ruleId: string;
  pairName: string;
  makerPlatform: string;
  makerPrice: number;
  counterPrice: number;
  contracts: number;
  tnaPercent: number;
  volumeUsd: number;
}

export interface EntryErrorEvent {
  ruleId: string;
  pairName: string;
  state: string;
  errorMessage: string;
}

/**
 * Strip the server's `_meta` envelope before the payload is used.
 * Mirrors `useUnwindingSocket`. Server wraps non-array payloads as
 * `{ ...payload, _meta }` and array payloads as `{ data: payload, _meta }`.
 */
/**
 * Phase 6 — project an `OpeningPageState` back into the legacy
 * `EnrichedEntryStatus` shape so existing row components keep working
 * during the bridging release. When `rule === null`, fills rule-scoped
 * fields with placeholders so the row still renders the market data.
 * Phase 7 removes this projection once the row components consume
 * `OpeningPageState` directly.
 *
 * On the `opening_state_update` path the envelope is a DELTA: the backend
 * omits the static blocks (market identity, rule config) while they have not
 * changed, so the fields they feed come out `undefined` here even though the
 * legacy type declares them required. That is the contract, not a hole —
 * `mergePreservedFields` restores the last known value before anything reads
 * the row, and the first frame for any pair always carries them in full (the
 * pod has no fingerprint for it yet), as does every seed.
 */
function projectToLegacy(p: OpeningPageState): EnrichedEntryStatus {
  const m = p.market;
  const r = p.rule;
  const displayPath: 'a' | 'b' | null =
    r?.committedPath ??
    (r?.sumPathA != null && r?.sumPathB != null
      ? r.sumPathA <= r.sumPathB
        ? 'a'
        : 'b'
      : null);
  const polyBid =
    displayPath === 'b' ? m.polyNoBid : m.polyYesBid;
  const polyAsk =
    displayPath === 'b' ? m.polyNoAsk : m.polyYesAsk;
  const kalshiBid =
    displayPath === 'b' ? m.kalshiYesBid : m.kalshiNoBid;
  const kalshiAsk =
    displayPath === 'b' ? m.kalshiYesAsk : m.kalshiNoAsk;
  return {
    ruleId: r?.ruleId ?? p.pairId,
    pairId: p.pairId,
    state: r?.state ?? 'no_rule',
    makerPlatform: r?.makerPlatform ?? 'kalshi',
    makerOrderId: r?.makerOrderId ?? null,
    makerPrice: r?.makerPrice ?? null,
    makerFilledQty: r?.makerFilledQty ?? 0,
    makerFilledPrice: r?.makerFilledPrice ?? 0,
    counterOrderId: r?.counterOrderId ?? null,
    counterFilledQty: r?.counterFilledQty ?? 0,
    lastStateChange: r?.lastStateChange ?? new Date().toISOString(),
    errorMessage: r?.errorMessage ?? null,
    pairName: m.pairName,
    expiryDate: m.expiryDate,
    daysToExpiry: m.daysToExpiry,
    polyBid,
    polyAsk,
    kalshiBid,
    kalshiAsk,
    currentSumOfAsks:
      r?.committedPath === 'b'
        ? r?.sumPathB ?? null
        : r?.committedPath === 'a'
          ? r?.sumPathA ?? null
          : (r?.sumPathA ?? r?.sumPathB ?? null),
    simulation: r?.simulation ?? {
      polyAsMaker: null,
      kalshiAsMaker: null
    },
    // NOT `?? null`: on a delta these are OMITTED while unchanged, and the
    // difference between "no news" (undefined → the merge keeps the last
    // value) and "the rule has none" (null) is the whole contract. With no
    // rule at all there is genuinely none.
    kalshiConfig: r ? r.kalshiConfig : null,
    polymarketConfig: r ? r.polymarketConfig : null,
    maxVolumeContracts: r?.maxVolumeContracts ?? 0,
    maxVolumeHit: r?.maxVolumeHit ?? null,
    volumePeriod: r?.volumePeriod ?? 'day',
    volumeUsed: r?.volumeUsed ?? 0,
    volumeRemaining: r?.volumeRemaining ?? 0,
    kalshiMarketUrl: m.kalshiMarketUrl,
    polymarketMarketUrl: m.polymarketMarketUrl,
    simTna: r?.simTna,
    simSize: r?.simSize,
    simMaxVolume: r?.simMaxVolume,
    appliedSimTna: r?.appliedSimTna,
    appliedSimSize: r?.appliedSimSize,
    appliedSimMaxVolume: r?.appliedSimMaxVolume,
    hasPendingSim: r?.hasPendingSim,
    polyYesBid: m.polyYesBid,
    polyYesAsk: m.polyYesAsk,
    polyNoBid: m.polyNoBid,
    polyNoAsk: m.polyNoAsk,
    kalshiYesBid: m.kalshiYesBid,
    kalshiYesAsk: m.kalshiYesAsk,
    kalshiNoBid: m.kalshiNoBid,
    kalshiNoAsk: m.kalshiNoAsk,
    // Sums + sim cells are computed at the envelope level (always,
    // rule or no rule) — read from `p.*` first so pre-rule rows
    // still see live values. Fall back to the rule slice for
    // backward-compat with older backend builds that emitted them
    // there.
    sumPathA: p.sumPathA ?? r?.sumPathA ?? null,
    sumPathB: p.sumPathB ?? r?.sumPathB ?? null,
    lowerPath: p.lowerPath ?? r?.lowerPath ?? null,
    committedPath: r?.committedPath ?? null,
    simPolyYesMaker: p.simPolyYesMaker ?? r?.simPolyYesMaker ?? null,
    simPolyNoMaker: p.simPolyNoMaker ?? r?.simPolyNoMaker ?? null,
    simKalshiYesMaker: p.simKalshiYesMaker ?? r?.simKalshiYesMaker ?? null,
    simKalshiNoMaker: p.simKalshiNoMaker ?? r?.simKalshiNoMaker ?? null,
    engineSim: p.engineSim ?? null,
    believedPosition: p.believedPosition ?? null,
    ruleCost: p.ruleCost ?? null,
    venuePositions: p.venuePositions ?? null,
    // Per-pair opener kill switch. Defaults false on manual-confirm, so
    // the row should render OFF when the field is unset. Forwarding it
    // verbatim here lets the row's switch read live state without
    // hitting REST.
    pairEnabled: p.pairEnabled === true,
    // The rule's REAL armed state — the AUTHORITATIVE source for the per-row
    // on/off switch. Forwarded VERBATIM (not coerced to false): `undefined`
    // must survive so the switch's back-compat fallback to `pairEnabled` can
    // kick in on envelopes from an older backend that doesn't emit it yet.
    ruleArmed: p.ruleArmed
  };
}

/**
 * The server's emit time off the `_meta` envelope. Freshness is aged against
 * this rather than the browser clock, so a skewed client cannot read a dead
 * venue poll as fresh. `null` on a payload without the envelope.
 */
export function readServerEmittedAt(message: unknown): number | null {
  const meta = (message as { _meta?: { serverEmittedAt?: unknown } } | null)
    ?._meta;
  const at = meta?.serverEmittedAt;
  return typeof at === 'number' && Number.isFinite(at) ? at : null;
}

function unwrap<T>(message: unknown): T {
  if (Array.isArray(message)) return message as T;
  if (
    message &&
    typeof message === 'object' &&
    '_meta' in (message as object)
  ) {
    const obj = message as Record<string, unknown> & { _meta?: unknown };
    if ('data' in obj) return obj.data as T;
    const rest = { ...obj };
    delete rest._meta;
    return rest as T;
  }
  return message as T;
}

/**
 * Compare two `TopOfBook | null` by VALUE. The projection re-allocates
 * these objects on every WS frame even when the quote is unchanged, so a
 * reference compare would never skip — we compare price + size instead.
 */
const topEqual = (
  a: TopOfBook | null | undefined,
  b: TopOfBook | null | undefined
): boolean => {
  if (a == null || b == null) return a == b;
  return a.price === b.price && a.size === b.size;
};

/** Cheap structural compare for the nested sim / position objects. */
const deepEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  if (typeof a !== 'object' || typeof b !== 'object') return false;
  const ak = Object.keys(a as object);
  const bk = Object.keys(b as object);
  if (ak.length !== bk.length) return false;
  for (const k of ak) {
    if (
      !deepEqual(
        (a as Record<string, unknown>)[k],
        (b as Record<string, unknown>)[k]
      )
    ) {
      return false;
    }
  }
  return true;
};

/**
 * Render-equality for the ACT cell's `ruleCost`. The backend re-emits every
 * rule's `RuleCost` on a cadence with a fresh `computedAt`, so comparing the
 * WHOLE object (incl. `computedAt` and the `spend/fees/matched/livePnl` fields
 * the ACT cell never renders) marks every rule-bearing row "changed" every ~2s
 * even when the displayed value is identical. Compare ONLY the two fields the
 * UI renders — `kalshiNet` / `polyNet` — treating absent objects as equal.
 * `ActNetCell` reads `computedAt` off the latest stored value at render time,
 * so excluding it here does not break the staleness check.
 */
const ruleCostRenderEqual = (
  a: EnrichedEntryStatus['ruleCost'],
  b: EnrichedEntryStatus['ruleCost']
): boolean => {
  if (!a || !b) return !a && !b;
  return a.kalshiNet === b.kalshiNet && a.polyNet === b.polyNet;
};

/**
 * Structural equality over the RENDERED fields of `EnrichedEntryStatus`
 * (the row cells, the sort metrics, and the engine-position drawer). When
 * `next` is render-equivalent to `prev` the store skips the update — no
 * version bump, no per-row notify. Fields the UI never reads (order ids,
 * filled qty, lastStateChange, etc.) are deliberately excluded so they
 * don't force a re-render on their own.
 */
const entryStateEqual = (
  prev: EnrichedEntryStatus,
  next: EnrichedEntryStatus
): boolean =>
  prev.pairEnabled === next.pairEnabled &&
  prev.ruleArmed === next.ruleArmed &&
  prev.state === next.state &&
  prev.pairName === next.pairName &&
  prev.expiryDate === next.expiryDate &&
  // `daysToExpiry` is NOT compared: it is `(expiryDate - now) / 86_400_000`,
  // recomputed by the pod on every envelope, so comparing it returned FALSE on
  // every frame for every row — the store's whole promise ("only rows whose
  // slice changed notify") silently did not hold, and every term after it in
  // this chain was unreachable. It carries no information `expiryDate` (right
  // above, and compared) does not already carry; only the clock moved.
  prev.makerPlatform === next.makerPlatform &&
  prev.makerPrice === next.makerPrice &&
  prev.errorMessage === next.errorMessage &&
  prev.currentSumOfAsks === next.currentSumOfAsks &&
  prev.sumPathA === next.sumPathA &&
  prev.sumPathB === next.sumPathB &&
  prev.lowerPath === next.lowerPath &&
  prev.committedPath === next.committedPath &&
  prev.kalshiMarketUrl === next.kalshiMarketUrl &&
  prev.polymarketMarketUrl === next.polymarketMarketUrl &&
  prev.maxVolumeContracts === next.maxVolumeContracts &&
  prev.volumePeriod === next.volumePeriod &&
  prev.volumeUsed === next.volumeUsed &&
  prev.volumeRemaining === next.volumeRemaining &&
  topEqual(prev.polyBid, next.polyBid) &&
  topEqual(prev.polyAsk, next.polyAsk) &&
  topEqual(prev.kalshiBid, next.kalshiBid) &&
  topEqual(prev.kalshiAsk, next.kalshiAsk) &&
  topEqual(prev.polyYesBid, next.polyYesBid) &&
  topEqual(prev.polyYesAsk, next.polyYesAsk) &&
  topEqual(prev.polyNoBid, next.polyNoBid) &&
  topEqual(prev.polyNoAsk, next.polyNoAsk) &&
  topEqual(prev.kalshiYesBid, next.kalshiYesBid) &&
  topEqual(prev.kalshiYesAsk, next.kalshiYesAsk) &&
  topEqual(prev.kalshiNoBid, next.kalshiNoBid) &&
  topEqual(prev.kalshiNoAsk, next.kalshiNoAsk) &&
  deepEqual(prev.engineSim, next.engineSim) &&
  deepEqual(prev.simulation, next.simulation) &&
  // The per-leg rule config. It used to ride EVERY frame, so leaving it out of
  // this comparison only meant a config-only frame was dropped and the next
  // frame carried the new value anyway. Now the pod sends it ONLY on the frame
  // where it changed, so dropping that frame loses the change until the next
  // reconnect seed. Costs nothing on a quiet row: an unchanged config is the
  // preserved reference and compares equal immediately.
  deepEqual(prev.kalshiConfig, next.kalshiConfig) &&
  deepEqual(prev.polymarketConfig, next.polymarketConfig) &&
  deepEqual(prev.believedPosition, next.believedPosition) &&
  ruleCostRenderEqual(prev.ruleCost, next.ruleCost) &&
  // Safe to deep-compare WHOLE: unlike `ruleCost`, the venue-positions payload
  // carries no `computedAt`/timestamp, so a re-poll that finds the same
  // contracts is byte-identical and never forces a re-render.
  deepEqual(prev.venuePositions, next.venuePositions) &&
  deepEqual(prev.maxVolumeHit, next.maxVolumeHit) &&
  deepEqual(prev.simPolyYesMaker, next.simPolyYesMaker) &&
  deepEqual(prev.simPolyNoMaker, next.simPolyNoMaker) &&
  deepEqual(prev.simKalshiYesMaker, next.simKalshiYesMaker) &&
  deepEqual(prev.simKalshiNoMaker, next.simKalshiNoMaker);

/**
 * Preserve the fields that ride the split-shape `opening_state*` stream ONLY
 * so a frame/seed that OMITS them never clobbers the value an earlier
 * `opening_state*` frame set. Mirrors the unwinder's `mergePreservedFields`.
 *
 * The hazard is `ruleArmed`: the `opening_state` WS envelope carries it, but
 * the `/states` REST seed (whose backend `EnrichedRuleState` payload has NO
 * `ruleArmed`) and any legacy `entry_status` frame OMIT it. Every path writes
 * this same store, so a seed arriving with `ruleArmed: undefined` — e.g. the
 * REST reseed that fires on every socket reconnect — would wipe the
 * authoritative armed bit. `resolveWsArmed` then falls back to `pairEnabled`,
 * so a PAUSED-but-pair-enabled rule (`ruleArmed:false, pairEnabled:true`)
 * would flip its per-row switch ON while the rule is actually paused — the
 * exact lie the durable-armed state exists to prevent. Preserve the last
 * known value whenever an incoming entry doesn't carry the field.
 *
 * `pairEnabled` / `engineSim` / `believedPosition` / `ruleCost` /
 * `venuePositions` share the same dual-path hazard (present on
 * `opening_state*`, absent from the REST seed), so they are preserved the same
 * way. For `venuePositions` the consequence is visible: the reseed fires on
 * EVERY socket reconnect, so without this the ACT / YES / NO cells would blank
 * out on each reconnect.
 */
/**
 * The flattened fields fed by the backend's static blocks. A `opening_state_update`
 * omits them while they are unchanged for that pair; the seed always carries
 * them. Kept as one list so the backend's `STATIC_MARKET_FIELDS` /
 * `STATIC_RULE_FIELDS` and this preservation cannot drift apart silently.
 */
const STATIC_DELTA_FIELDS = [
  'pairName',
  'expiryDate',
  'kalshiMarketUrl',
  'polymarketMarketUrl',
  'kalshiConfig',
  'polymarketConfig'
] as const satisfies readonly (keyof EnrichedEntryStatus)[];

/**
 * A delta is only meaningful ON TOP of a base. The pod omits the static blocks
 * only after it has sent them, so every delta for a row we already hold is
 * complete enough.
 *
 * This guard was introduced for an ordering inversion that no longer exists:
 * the listeners used to be separate `useSocketEvent` hooks whose effects ran in
 * DECLARATION order, so a delta emitted BEFORE a key frame was applied AFTER
 * it, re-creating the row it had just evicted. They are one effect with direct
 * `socket.on` handlers now, which fire in ARRIVAL order.
 *
 * It stays because the invariant is the FE's own: a delta without a base cannot
 * be rendered as anything but a half-blank row, and this side has no other way
 * to check that. It is the last line behind the pod's fingerprint bookkeeping.
 *
 * So: with no base, a frame missing any static field is DROPPED rather than
 * stored as a half-row. It costs at most one tick — the pod forgot that pair's
 * fingerprint in the same operation that emitted the key frame, so its next
 * frame carries everything. A genuinely NEW pair is unaffected: the pod has no
 * fingerprint for it either, so its first frame is full.
 */
function canApplyDelta(
  store: LiveStore<EnrichedEntryStatus>,
  s: EnrichedEntryStatus
): boolean {
  // The CANONICAL key — `keysOf(item)[0]` — because that is the key
  // `applyUpdate` takes its merge base from. Accepting on the pairId while the
  // merge reads the ruleId would pass a frame whose base is absent, which is
  // exactly the coupling that produced the bug this guard exists for.
  const canonical = s.ruleId || s.pairId;
  if (canonical && store.getKeySnapshot(canonical)) return true;
  if (STATIC_DELTA_FIELDS.every(field => s[field] !== undefined)) return true;
  // Should be unreachable in steady state; if it ever fires repeatedly, that
  // repetition IS the signal.
  console.warn(
    `[entry] dropped a delta for ${canonical}: no stored row and the static ` +
      'blocks are absent, so the row could only be built half-blank'
  );
  return false;
}

function mergePreservedFields(
  prev: EnrichedEntryStatus | undefined,
  incoming: EnrichedEntryStatus
): EnrichedEntryStatus {
  if (!prev) return incoming;
  let out = incoming;
  if (incoming.ruleArmed === undefined && prev.ruleArmed !== undefined) {
    out = { ...out, ruleArmed: prev.ruleArmed };
  }
  if (incoming.pairEnabled === undefined && prev.pairEnabled !== undefined) {
    out = { ...out, pairEnabled: prev.pairEnabled };
  }
  if (incoming.engineSim === undefined && prev.engineSim !== undefined) {
    out = { ...out, engineSim: prev.engineSim };
  }
  if (
    incoming.believedPosition === undefined &&
    prev.believedPosition !== undefined
  ) {
    out = { ...out, believedPosition: prev.believedPosition };
  }
  if (incoming.ruleCost === undefined && prev.ruleCost !== undefined) {
    out = { ...out, ruleCost: prev.ruleCost };
  }
  if (
    incoming.venuePositions === undefined &&
    prev.venuePositions !== undefined
  ) {
    out = { ...out, venuePositions: prev.venuePositions };
  }
  // The static blocks the backend OMITS from a delta while they have not
  // changed (`STATIC_MARKET_FIELDS` / `STATIC_RULE_FIELDS` on the pod). They
  // are the same bytes on every 500ms frame otherwise — the market identity
  // and the operator-owned rule config — and re-sending them ~96 times a
  // second is what buried the socket's in-band ping. Same rule as above:
  // `undefined` is "no news", so keep what we had.
  for (const field of STATIC_DELTA_FIELDS) {
    if (incoming[field] === undefined && prev[field] !== undefined) {
      out = { ...out, [field]: prev[field] };
    }
  }
  return out;
}

/**
 * Factory for the opener live store. Dual-keyed by `ruleId` AND `pairId`
 * (deduped) mirroring the legacy `pairId ?? ruleId` lookup. `merge` ports the
 * unwinder's field-preservation so a frame/seed omitting `ruleArmed` (the
 * `/states` REST reseed on reconnect / a legacy frame) never clobbers the
 * authoritative armed bit the per-row switch reads.
 */
export function createEntryLiveStore(): LiveStore<EnrichedEntryStatus> {
  return createLiveStore<EnrichedEntryStatus>({
    keysOf: s => {
      const keys: string[] = [];
      if (s.ruleId) keys.push(s.ruleId);
      if (s.pairId && s.pairId !== s.ruleId) keys.push(s.pairId);
      return keys;
    },
    equals: entryStateEqual,
    merge: mergePreservedFields
  });
}

/* ------------------------------------------------------------------ */
/* Socket target resolution (group feed pods vs core gateway)          */
/* ------------------------------------------------------------------ */

export interface EntrySocketTarget {
  url: string;
  path?: string;
}

interface EntrySocketEnv {
  VITE_OPENER_FEED_URL?: string;
  VITE_POSITION_OPENER_URL?: string;
}

/** Today's default target — the always-on core gateway. */
export function coreEntrySocketTarget(
  env: EntrySocketEnv = import.meta.env
): EntrySocketTarget {
  return { url: env.VITE_POSITION_OPENER_URL || '' };
}

/**
 * Pick the Socket.io target for the current group selection. A specific
 * group whose feed pod is Ready connects to the shared opener-feed ALB on
 * the group's path; everything else ('all', 'ungrouped', group not ready,
 * group unknown, no pod state, env var unset) uses the core gateway —
 * the 'all'/'ungrouped' views NEVER depend on feed pods.
 */
export function resolveEntrySocketTarget(
  selection: GroupSelection | null | undefined,
  podState: OpenerGroupPodState[] | null | undefined,
  env: EntrySocketEnv = import.meta.env
): EntrySocketTarget {
  const core = coreEntrySocketTarget(env);
  if (!selection || selection.kind !== 'group') return core;
  if (!env.VITE_OPENER_FEED_URL) return core;
  const group = podState?.find(g => g.groupId === selection.groupId);
  if (!group || group.feedReady !== true) return core;
  return {
    url: env.VITE_OPENER_FEED_URL,
    path: `/groups/${selection.groupId}/socket.io`
  };
}

/**
 * Layer the connect_error fallback on top of the resolution: once a feed
 * target has failed to connect, stick to the core gateway until the
 * selection changes (the hook resets the flag on selection change).
 */
export function effectiveEntrySocketTarget(
  selection: GroupSelection | null | undefined,
  podState: OpenerGroupPodState[] | null | undefined,
  feedFallback: boolean,
  env: EntrySocketEnv = import.meta.env
): EntrySocketTarget {
  const resolved = resolveEntrySocketTarget(selection, podState, env);
  return feedFallback && resolved.path ? coreEntrySocketTarget(env) : resolved;
}

/**
 * The axios instance the REST page seed must use: whichever pod the SOCKET
 * resolved to, never a separate decision.
 *
 * The seed used to be hardwired to `positionOpenerAxiosApi` (core) even while
 * the socket was connected to the group's feed pod. It fires on every page load
 * AND on every socket reconnect, so the always-on core pod rebuilt
 * `getEnrichedStates()` over the FULL pair universe — synchronously, on the one
 * event loop that also serves `/bootstrap` — every time an operator's socket
 * dropped and came back. It would also stop working entirely once
 * `StatesController` goes feed-only.
 *
 * So: the seed FOLLOWS the socket's target. It is derived from the same
 * `EntrySocketTarget`, which means the sticky core fallback after a feed
 * `connect_error` carries the seed along with it. A `path` on the target
 * implies `VITE_OPENER_FEED_URL` is set (that is what
 * `resolveEntrySocketTarget` checks before returning one), so
 * `resolveOpenerApi` cannot throw `UnresolvedOpenerGroupError` here.
 *
 * The ONE place the two are allowed to diverge is the seed effect's own
 * fallback: a feed-pod seed that fails retries against core once while the
 * socket stays on the feed pod. That is deliberate and self-healing — see the
 * effect. Any OTHER read added to this hook should follow the target, not
 * invent a third rule.
 */
export function resolveEntrySeedApi(
  selection: GroupSelection | null | undefined,
  target: EntrySocketTarget
): AxiosInstance {
  if (target.path && selection?.kind === 'group') {
    return resolveOpenerApi(selection.groupId);
  }
  return positionOpenerAxiosApi;
}

export function useEntrySocket(groupSelection?: GroupSelection) {
  const accessToken = _getToken();

  // Pod state only matters while a specific group is selected — the poll
  // is gated off for the 'all'/'ungrouped' views (always core target).
  const { data: podState } = useOpenerGroupsPodState({
    enabled: groupSelection?.kind === 'group'
  });

  // Sticky core fallback after a feed connect_error; reset when the
  // operator switches the group selection.
  const [feedFallback, setFeedFallback] = useState(false);
  const selectionKey = groupSelection
    ? groupSelectionKey(groupSelection)
    : 'all';
  useEffect(() => {
    setFeedFallback(false);
  }, [selectionKey]);

  const target = effectiveEntrySocketTarget(
    groupSelection,
    podState,
    feedFallback
  );
  const wsUrl = target.url;
  // Same pod as the socket. `resolveOpenerAxios` caches per base URL, so this
  // reference is stable across renders and safe as an effect dependency —
  // which it has to be: locally `VITE_OPENER_FEED_URL` and
  // `VITE_POSITION_OPENER_URL` are the same host, so `wsUrl` alone does not
  // change when the target flips between core and a group's feed pod.
  const seedApi = resolveEntrySeedApi(groupSelection, target);

  const { socket } = useSocket((wsUrl || '') + '/opening', {
    enabled: !!wsUrl && !!accessToken,
    transports: ['websocket'],
    ...(target.path ? { path: target.path } : {}),
    auth: (cb: (data: { token: string }) => void) =>
      cb({ token: _getToken() })
  });

  // While connected through a feed pod, a connect_error (pod gone, ALB
  // path dead, auth handshake refused) degrades to the core gateway.
  useEffect(() => {
    if (!target.path) return;
    const onConnectError = () => setFeedFallback(true);
    socket.on('connect_error', onConnectError);
    return () => {
      socket.off('connect_error', onConnectError);
    };
  }, [socket, target.path]);

  // The engine's per-GROUP live trading summary (descalce + contracts + money),
  // pushed on the `/opening` socket as `group_cost_update`. The payload now
  // carries its own `groupId` (the feed pod stamps it), and the strip rejects a
  // summary whose `groupId` doesn't match the selected group's `_id` — so a
  // stale or cross-group aggregate can't bleed under a new group's header.
  // Still cleared whenever the socket target changes (group switch / core↔feed
  // fallback) so the previous group's summary doesn't linger during handoff.
  const groupCostStoreRef = useRef<ValueStore<GroupCostSummary | null> | null>(
    null
  );
  if (groupCostStoreRef.current === null) {
    groupCostStoreRef.current = createValueStore<GroupCostSummary | null>(null);
  }
  const groupCostStore = groupCostStoreRef.current;

  // Provenance + freshness for the per-pair `venuePositions`, pushed by the
  // same pod that serves the pairs, kept WITH its receive time and the server's
  // emit time so freshness can be aged in durations instead of across clocks.
  // `null` = no meta seen on this socket (the CORE gateway case).
  const [venueFrame, setVenueFrame] =
    useState<VenuePositionsMetaFrame | null>(null);

  // Tracks WHICH socket instance we subscribed on — switching targets
  // (core ↔ feed, group ↔ group) hands us a new socket that needs its own
  // subscribe_entry even though the previous one already subscribed.
  const subscribedSocket = useRef<unknown>(null);

  // Per-pair external store for the high-frequency live price/sim state.
  // WS frames mutate it in plain JS (no setState) so only rows whose pair
  // actually changed re-render. Created once per hook instance.
  const storeRef = useRef<LiveStore<EnrichedEntryStatus> | null>(null);
  if (storeRef.current === null) storeRef.current = createEntryLiveStore();
  const entryLiveStore = storeRef.current;

  // The store holds its notifications while the tab is hidden; repaint the
  // moment it comes back rather than waiting for the next incoming frame.
  useFlushOnVisible(entryLiveStore);

  // REST seed: same pattern as `useUnwindingSocket` — fetch the latest
  // EnrichedEntryStatus[] snapshot when the WS isn't connected so the page
  // has data during the handshake or while the predictions subsystem is
  // OFF. Live WS deltas always override.
  //
  // A seed against a group's feed pod falls back to core ONCE. A feed pod only
  // serves `/groups/{gid}/api/position-opener/states` from the image that
  // carries that route in its allowlist, and group pods are long-lived — a
  // group that has not been cycled since the backend rolled still 404s it. The
  // page must not lose its pre-WS snapshot over that: `disconnect` (a ping
  // timeout on a busy group, the exact case this seed exists for) does NOT set
  // `feedFallback` — only `connect_error` does — so the target stays on the
  // feed pod for the whole reconnect window. Falling back keeps the deploy
  // order a preference rather than a correctness requirement.
  useEffect(() => {
    if (!wsUrl || !accessToken) return;
    if (socket.connected) return;

    let cancelled = false;
    const apply = (data: EnrichedEntryStatus[]) => {
      if (cancelled) return;
      if (!Array.isArray(data)) return;
      entryLiveStore.applyUpdate(data);
    };

    seedApi
      .get<EnrichedEntryStatus[]>('/states')
      .then(({ data }) => apply(data))
      .catch(err => {
        if (cancelled) return;
        if (seedApi === positionOpenerAxiosApi) {
          console.warn('[EntrySocket] REST seed failed:', err?.message ?? err);
          return;
        }
        console.warn(
          '[EntrySocket] feed-pod REST seed failed, falling back to core:',
          err?.message ?? err
        );
        positionOpenerAxiosApi
          .get<EnrichedEntryStatus[]>('/states')
          .then(({ data }) => apply(data))
          .catch(coreErr => {
            if (cancelled) return;
            console.warn(
              '[EntrySocket] core REST seed also failed:',
              coreErr?.message ?? coreErr
            );
          });
      });

    return () => {
      cancelled = true;
    };
  }, [wsUrl, seedApi, accessToken, socket.connected, entryLiveStore]);

  const latencyBuf = useRef<LatencySample[]>([]);
  useLatencyTracker(socket, latencyBuf);

  // ── The socket listeners ────────────────────────────────────────────────
  //
  // ONE effect, `socket.on` directly. Deliberately NOT `useSocketEvent`: that
  // hook stores every message in React state and calls `rerender({})`, so each
  // frame re-rendered `EntryPage` (686 lines, 22 memos) and through it
  // `EntryTabByPath` (~4.9k lines). Measured in production before this change:
  // `frontend.reactRenderMs` p95 **16.4s** between receiving a frame and
  // painting it — which is also what starved the engine.io PONG and got the
  // socket dropped every ~85s with `reason=ping timeout`.
  //
  // `entryLiveStore` exists precisely so a frame costs ZERO page renders: rows
  // subscribe per key (`useLiveState` → `useSyncExternalStore`) and only the
  // one whose data moved repaints. Routing the frames through React state
  // defeated that. So: the high-frequency events write straight to the store,
  // `group_cost_update` writes to its own value store, and only
  // `venue_positions_meta` (~1 per 10s) is still page state — it resolves the
  // ACCOUNT scope for the whole table and has to live at page level.
  //
  // `recordReceived` stays on ALL SEVEN events: that telemetry is what
  // diagnosed this, and it is what measures whether the fix worked.
  //
  // This hook deliberately does NOT listen to the legacy `entry_status` /
  // `entry_status_update` events. The backend emits them alongside
  // `opening_state*` for old FE builds during a deploy, but they carry the
  // legacy `EnrichedRuleState` shape WITHOUT `engineSim`. Consuming both
  // streams into one Map (last-write-wins) made every `entry_status_update`
  // clobber the `engineSim` that the preceding `opening_state_update` had set
  // — the Sim columns flickered value ↔ "—" on every tick. `opening_state*`
  // is a superset (every manual-confirmed pair, rule or not), so it is the
  // single source of truth here. Do not re-add the legacy listeners.
  useEffect(() => {
    const buf = latencyBuf;

    const onOpeningState = (message: unknown) => {
      recordReceived(buf.current, 'opening_state', message);
      const payload = unwrap<OpeningPageState[]>(message);
      if (!Array.isArray(payload)) return;
      // Full snapshot → evicts pairs no longer present.
      entryLiveStore.applySnapshot(payload.map(projectToLegacy));
    };

    const onOpeningStateUpdate = (message: unknown) => {
      recordReceived(buf.current, 'opening_state_update', message);
      const payload = unwrap<OpeningPageState[]>(message);
      if (!Array.isArray(payload)) return;
      entryLiveStore.applyUpdate(
        payload
          .map(projectToLegacy)
          .filter(state => canApplyDelta(entryLiveStore, state))
      );
    };

    const onOpeningStateKeys = (message: unknown) => {
      // No shape guard here on purpose: `retainKeys` is the ONE gate that
      // decides what a malformed or empty key list means, so every caller
      // gets the same answer. A copy of it here would be a branch no test
      // could reach.
      const payload = unwrap<{ keys: string[] }>(message);
      entryLiveStore.retainKeys(payload?.keys);
    };

    const onGroupCost = (message: unknown) => {
      recordReceived(buf.current, 'group_cost_update', message);
      groupCostStore.set(unwrap<GroupCostSummary>(message));
    };

    const onVenueMeta = (message: unknown) => {
      recordReceived(buf.current, 'venue_positions_meta', message);
      setVenueFrame({
        meta: unwrap<VenuePositionsMeta>(message),
        receivedAt: Date.now(),
        receivedMono: performance.now(),
        serverEmittedAt: readServerEmittedAt(message)
      });
    };

    // Nothing reads these three — the reducer they used to feed was dead code
    // (`EntryPage` destructures only `isConnected`, `store`, `venueFrame`).
    // The listeners stay ONLY so their frames keep feeding the latency
    // telemetry; they intentionally hold no state.
    const onExecution = (message: unknown) =>
      recordReceived(buf.current, 'entry_execution', message);
    const onError = (message: unknown) =>
      recordReceived(buf.current, 'entry_error', message);
    const onOrders = (message: unknown) =>
      recordReceived(buf.current, 'entry_orders', message);

    socket.on('opening_state', onOpeningState);
    socket.on('opening_state_update', onOpeningStateUpdate);
    socket.on('opening_state_keys', onOpeningStateKeys);
    socket.on('group_cost_update', onGroupCost);
    socket.on('venue_positions_meta', onVenueMeta);
    socket.on('entry_execution', onExecution);
    socket.on('entry_error', onError);
    socket.on('entry_orders', onOrders);

    return () => {
      socket.off('opening_state', onOpeningState);
      socket.off('opening_state_update', onOpeningStateUpdate);
      socket.off('opening_state_keys', onOpeningStateKeys);
      socket.off('group_cost_update', onGroupCost);
      socket.off('venue_positions_meta', onVenueMeta);
      socket.off('entry_execution', onExecution);
      socket.off('entry_error', onError);
      socket.off('entry_orders', onOrders);
    };
  }, [socket, entryLiveStore, groupCostStore]);

  // NOTE ON ORDER: the listener effect above is declared BEFORE this one on
  // purpose. Effects run in declaration order, so the handlers are registered
  // before `subscribe_entry` asks the pod for the seed. `useSocketEvent` used
  // to make that irrelevant by RETAINING the last message; direct listeners
  // only ever see future ones, so "ask, then listen" would be a real gap the
  // day a payload arrives synchronously.
  // Subscribe on every connect/reconnect.
  useEffect(() => {
    const subscribeIfNeeded = () => {
      if (subscribedSocket.current !== socket) {
        socket.emit('subscribe_entry');
        subscribedSocket.current = socket;
      }
    };
    const markDisconnected = () => {
      subscribedSocket.current = null;
    };

    socket.on('connect', subscribeIfNeeded);
    socket.on('disconnect', markDisconnected);

    if (socket.connected) subscribeIfNeeded();
    else markDisconnected();

    return () => {
      socket.off('connect', subscribeIfNeeded);
      socket.off('disconnect', markDisconnected);
    };
  }, [socket, socket.connected]);

  // Clear the per-GROUP cost summary the moment the socket target changes — a
  // new group (or the core↔feed fallback) hands us a fresh socket, and the
  // held value would otherwise sit above the new pod's rows until it emits.
  useEffect(() => {
    groupCostStore.set(null);
  }, [socket, groupCostStore]);

  // The venue meta is reset only when the operator switches to a DIFFERENT
  // group — NOT on a same-group socket swap (a reconnect after a ping timeout,
  // or the core↔feed fallback). Cross-group safety is already enforced at render
  // by the `groupId` guard below (`activeVenueFrame`), so clearing on the raw
  // socket identity was redundant AND was what blanked the ACT / YES / NO cells
  // on every reconnect: a `testAccount` group (whose main-account REST may NOT
  // stand in) flickered value ↔ "—" each time its busy feed pod dropped and
  // re-established the socket.
  const venueGroupId =
    groupSelection?.kind === 'group' ? groupSelection.groupId : null;
  useEffect(() => {
    setVenueFrame(null);
  }, [venueGroupId]);

  // Stable `getState` — a non-reactive point read off the live store. The
  // high-frequency price/sim stream now lives in `entryLiveStore` (rows
  // subscribe per-key via `useLiveState`), so this no longer flips
  // identity on a WS tick. Kept for non-row callers that want a one-shot
  // snapshot (e.g. the "Engine position" drawer reading `believedPosition`
  // for the selected rule). Row components must NOT use this — they read
  // reactively from the store so only the changed row re-renders.
  const getState = useCallback(
    (idOrPair: string): EnrichedEntryStatus | undefined =>
      entryLiveStore.getKeySnapshot(idOrPair),
    [entryLiveStore]
  );

  const injectStates = useCallback(
    (states: EnrichedEntryStatus[]) => {
      if (!Array.isArray(states)) {
        console.warn(
          '[EntrySocket] injectStates called with non-array, ignoring:',
          states
        );
        return;
      }
      entryLiveStore.applyUpdate(states);
    },
    [entryLiveStore]
  );

  // The meta is only ever this GROUP's meta. Clearing it in an effect on the
  // socket change leaves one render where the previous group's account label
  // and staleness horizon sit above the newly selected group's rows (the
  // socket ref updates in an effect too), so the match is made HERE, during
  // render: a frame whose `groupId` is not the selected group is not shown at
  // all. Same rejection the group-cost strip does with its own `groupId`.
  const activeVenueFrame =
    venueFrame &&
    groupSelection?.kind === 'group' &&
    venueFrame.meta.groupId === groupSelection.groupId
      ? venueFrame
      : null;

  return {
    isConnected: socket.connected,
    store: entryLiveStore,
    getState,
    injectStates,
    groupCostStore,
    venueFrame: activeVenueFrame
  };
}
